/* eslint-disable no-console */
/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * Logger utility for the application
 * Provides structured logging with environment-aware output
 * 
 * @example
 * logger.info('User logged in', { userId: 123 });
 * logger.error('Database error', error);
 * logger.debug('Processing ballot', { ballotId: 456 });
 */

export type LogLevel = 'debug' | 'info' | 'warn' | 'error';

interface LogMeta {
  [key: string]: unknown;
}

class Logger {
  private isDevelopment = process.env.NODE_ENV === 'development';
  private isTest = process.env.NODE_ENV === 'test';
  private isProduction = process.env.NODE_ENV === 'production';
  private logLevel: LogLevel = (process.env.LOG_LEVEL as LogLevel) || 'info';

  private levels = { debug: 0, info: 1, warn: 2, error: 3 };

  private shouldLog(level: LogLevel): boolean {
    if (this.isTest) return false; // Suppress logs in tests
    return this.levels[level] >= this.levels[this.logLevel];
  }

  private formatMessage(level: string, message: string, meta?: LogMeta): string {
    const timestamp = new Date().toISOString();
    const metaStr = meta && Object.keys(meta).length > 0 ? ` ${JSON.stringify(meta)}` : '';
    
    if (this.isProduction) {
      // Structured JSON logging for production (easier to parse)
      return JSON.stringify({ timestamp, level, message, ...meta });
    }
    
    // Human-readable format for development
    return `[${timestamp}] [${level}] ${message}${metaStr}`;
  }

  /**
   * Log informational messages (development and production)
   */
  info(message: string, meta?: LogMeta): void {
    if (this.shouldLog('info')) {
      console.log(this.formatMessage('INFO', message, meta));
    }
  }

  /**
   * Log warning messages (always logged except in tests)
   */
  warn(message: string, meta?: LogMeta): void {
    if (this.shouldLog('warn')) {
       
      console.warn(this.formatMessage('WARN', message, meta));
    }
  }

  /**
   * Log error messages (always logged except in tests)
   * Integrates with Sentry if SENTRY_DSN is configured
   */
  error(message: string, error?: Error | any, meta?: LogMeta): void {
    if (this.shouldLog('error')) {
      const errorMeta = { ...meta };
      if (error instanceof Error) {
        errorMeta.stack = error.stack;
        errorMeta.errorMessage = error.message;
      } else if (error) {
        errorMeta.error = error;
      }
      
       
      console.error(this.formatMessage('ERROR', message, errorMeta));

      // Sentry integration (if configured)
      if (this.isProduction && typeof window !== 'undefined' && (window as any).Sentry) {
        (window as any).Sentry.captureException(error || new Error(message), {
          contexts: { metadata: meta }
        });
      }
    }
  }

  /**
   * Log debug messages (only in development)
   */
  debug(message: string, meta?: LogMeta): void {
    if (this.shouldLog('debug')) {
      console.debug(this.formatMessage('DEBUG', message, meta));
    }
  }

  /**
   * Log SQL queries (only in development)
   */
   
  query(query: string, params?: unknown[]): void {
    if (this.isDevelopment && this.shouldLog('debug')) {
      console.log(this.formatMessage('SQL', query, { params }));
    }
  }

  /**
   * Log HTTP requests
   */
  http(method: string, path: string, status?: number, meta?: LogMeta): void {
    if (this.shouldLog('info')) {
      const httpMeta = { method, path, status, ...meta };
      console.log(this.formatMessage('HTTP', `${method} ${path}${status ? ` - ${status}` : ''}`, httpMeta));
    }
  }

  /**
   * Log email operations (development and info level)
   */
  email(action: string, recipient: string, meta?: LogMeta): void {
    if (this.shouldLog('info')) {
      const emailMeta = { action, recipient, ...meta };
      this.info(`Email: ${action}`, emailMeta);
    }
  }

  /**
   * Log authentication events
   */
  auth(action: string, meta?: LogMeta): void {
    if (this.shouldLog('info')) {
      this.info(`Auth: ${action}`, meta);
    }
  }

  /**
   * Log election-specific events
   */
  election(action: string, electionId: string, meta?: LogMeta): void {
    if (this.shouldLog('info')) {
      const electionMeta = { electionId, ...meta };
      this.info(`Election: ${action}`, electionMeta);
    }
  }
}

export const logger = new Logger();
